import { getStatusLabel } from './jobStatuses';
import { getPriorityLabel } from './jobPriorities';
import { getJobTechnicianStatusLabel } from './jobTechnicianRequests';
import { formatWallClockDateTime } from './invoiceDateTime';

const FIELD_LABELS = {
  status: 'Status',
  priority: 'Priority',
  description: 'Description',
  city: 'City',
  state: 'State',
  address: 'Address',
  admin_id: 'Admin',
  dispatcher_id: 'Dispatcher',
  client_id: 'Client',
  trade: 'Trade',
  nte: 'NTE',
  zip_code: 'Zip Code',
  latitude: 'Latitude',
  longitude: 'Longitude',
  work_order: 'Work Order',
  store_name: 'Store Name',
  notes: 'Notes',
  sla: 'SLA',
  quickbooks_invoiced: 'QuickBooks Invoiced',
  factored: 'Factored',
  paid: 'Paid',
  paid_date: 'Paid Date',
  sent_to_client: 'Sent To Client',
  category: 'Category',
  is_private: 'Private',
  invoice_number: 'Invoice Number',
  technician_name: 'Technician',
  tech_cost: 'Tech Cost',
  technician_status: 'Technician Status',
  scheduled_date: 'Scheduled Date',
  part_name: 'Part Name',
  part_description: 'Part Description',
  part_url: 'Part URL',
  part_cost: 'Part Cost',
  part_order_status: 'Part Order Status',
  part_notes: 'Part Notes',
  part_technician_id: 'Part Technician',
  comment_text: 'Comment',
  proposal_text: 'Proposal Text',
  proposal_price: 'Proposal Price',
  proposal_item_count: 'Proposal Items',
  is_approved: 'Approved',
  client_approval: 'Client Approved',
  invoiced: 'Invoiced',
  url: 'URL',
  label: 'Label',
};

const formatBoolean = (value) => (value ? 'Yes' : 'No');

const DATE_FIELD_SUFFIXES = ['_at', '_date'];

export const formatShortDateTime = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const isDateField = (field) => DATE_FIELD_SUFFIXES.some((suffix) => field?.endsWith(suffix));

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return 'empty';
  if (field === 'status') return getStatusLabel(Number(value));
  if (field === 'priority') return getPriorityLabel(value);
  if (field === 'technician_status') return getJobTechnicianStatusLabel(value);
  if (isDateField(field)) {
    const formatted = formatShortDateTime(value);
    if (formatted) return formatted;
  }
  if (typeof value === 'boolean') return formatBoolean(value);
  return String(value);
};

const formatChanges = (changes = []) => {
  if (!Array.isArray(changes) || changes.length === 0) return null;
  return changes
    .map((change) => {
      const field = FIELD_LABELS[change.field] || change.field;
      const fromValue = formatFieldValue(change.field, change.from);
      const toValue = formatFieldValue(change.field, change.to);
      return `${field}: ${fromValue} -> ${toValue}`;
    })
    .join('\n');
};

const ACTION_MESSAGES = {
  'job.created': 'Job created',
  'job.updated': 'Job updated',
  'job.deleted': 'Job deleted',
  'job.quickbooks_invoiced_changed': 'QuickBooks invoiced updated',
  'job.factored_changed': 'Factored updated',
  'job.paid_changed': 'Paid status updated',
  'job.sent_to_client_changed': 'Sent to client updated',
  'job.notes_changed': 'Notes updated',
  'job.locked': 'Job locked',
  'job.unlocked': 'Job unlocked',
  'job.unlock_requested': 'Job unlock requested',
  'job.link_created': 'Job link created',
  'job.link_updated': 'Job link updated',
  'job.link_deleted': 'Job link deleted',
  'proposal.created': 'Proposal created',
  'proposal.updated': 'Proposal updated',
  'proposal.deleted': 'Proposal deleted',
  'proposal.approved': 'Proposal approval updated',
  'proposal.client_approved': 'Proposal client approval updated',
  'proposal.locked': 'Proposal locked',
  'proposal.unlocked': 'Proposal unlocked',
  'proposal.approval_requested': 'Proposal approval requested',
  'proposal.unlock_requested': 'Proposal unlock requested',
  'proposal.rejected': 'Proposal rejected',
  'proposal.invoiced': 'Proposal invoiced',
  'proposal.uninvoiced': 'Proposal uninvoiced',
  'proposal.invoice_number_updated': 'Proposal invoice number updated',
  'part.created': 'Part created',
  'part.updated': 'Part updated',
  'part.deleted': 'Part deleted',
  'part.approved': 'Part approval updated',
  'part.locked': 'Part locked',
  'part.unlocked': 'Part unlocked',
  'part.approval_requested': 'Part approval requested',
  'part.unlock_requested': 'Part unlock requested',
  'part.rejected': 'Part rejected',
  'technician_assignment.created': 'Technician assignment created',
  'technician_assignment.updated': 'Technician assignment updated',
  'technician_assignment.deleted': 'Technician assignment deleted',
  'technician_assignment.locked': 'Technician assignment locked',
  'technician_assignment.unlocked': 'Technician assignment unlocked',
  'technician_assignment.unlock_requested': 'Technician assignment unlock requested',
  'comment.created': 'Comment added',
  'attachment.uploaded': 'Attachment uploaded',
  'attachment.deleted': 'Attachment deleted',
  'attachment.category_updated': 'Attachment category updated',
  'attachment.privacy_updated': 'Attachment privacy updated',
};

export const formatJobLogEntry = (log) => {
  const baseMessage = ACTION_MESSAGES[log?.action] || log?.action || 'Job activity';
  const commentChange = (log?.metadata?.changes || []).find((change) => change.field === 'comment_text');
  if (log?.action === 'comment.created' && commentChange?.to) {
    return `${baseMessage}: "${commentChange.to}"`;
  }

  // proposal.invoiced stores invoice_number / invoiced_at at the metadata root
  // (not under changes) — surface them like invoice-number edit rows.
  if (log?.action === 'proposal.invoiced') {
    const invoiceParts = [];
    if (log.metadata?.invoice_number) {
      invoiceParts.push(`Invoice #: ${log.metadata.invoice_number}`);
    }
    if (log.metadata?.invoiced_at) {
      const wallClock = formatWallClockDateTime(log.metadata.invoiced_at);
      invoiceParts.push(`Date: ${wallClock || log.metadata.invoiced_at}`);
    }
    if (invoiceParts.length > 0) {
      return `${baseMessage}: ${invoiceParts.join(' · ')}`;
    }
  }

  const changesSummary = formatChanges(log?.metadata?.changes);
  const reason = log?.metadata?.reason;
  const label = log?.metadata?.label;

  const details = [label ? `Item: ${label}` : null, changesSummary, reason ? `Reason: ${reason}` : null]
    .filter(Boolean)
    .join('\n');
  return details ? `${baseMessage}: ${details}` : baseMessage;
};
